import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

const NONE = "__none";
const F = ({ label, children, span }) => <div className={`space-y-1.5 ${span ? "sm:col-span-2" : ""}`}><Label>{label}</Label>{children}</div>;
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

export const AssignDialog = ({ item, date, stationId, drivers, vehicles, telepass, doubleCrew, onClose, onSaved }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) setF({ driver_id: item.driver_id || "", co_driver_id: item.co_driver_id || NONE, vehicle_id: item.vehicle_id || (item.plate ? NONE : ""), plate: item.plate || "",
      telepass_id: item.telepass_id || NONE, fuel_amount: item.fuel_amount ?? "", km_start: item.km_start ?? "", km_end: item.km_end ?? "", notes: item.notes || "",
      swap_km: item.swap_km ?? "", swap_time: item.swap_time || "" });
  }, [item]);
  if (!item) return null;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const ks = num(f.km_start), ke = num(f.km_end), sw = num(f.swap_km);
  const hasCo = f.co_driver_id && f.co_driver_id !== NONE;
  const swapBad = hasCo && sw !== null && (!f.swap_time || (ks !== null && ke !== null && (sw < ks || sw > ke)));

  const save = async () => {
    setBusy(true);
    try {
      const payload = { date: item.date || date, station_id: stationId, driver_id: f.driver_id, vehicle_id: f.vehicle_id && f.vehicle_id !== NONE ? f.vehicle_id : null,
        plate: f.plate, telepass_id: f.telepass_id === NONE ? null : f.telepass_id, fuel_amount: num(f.fuel_amount), km_start: ks, km_end: ke, notes: f.notes,
        co_driver_id: hasCo ? f.co_driver_id : null, swap_km: hasCo ? sw : null, swap_time: hasCo && sw !== null ? f.swap_time : null };
      if (item.id) await api.put(`/assignments/${item.id}`, payload); else await api.post("/assignments", payload);
      toast.success("Assegnazione salvata");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>{item.id ? `Assegnazione ${item.driver_name}` : "Nuova assegnazione"}</DialogTitle>
          <DialogDescription>I km li inserisce di solito il driver dal giornale di bordo; qui puoi correggerli a mano.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Driver" span>
            <PlateCombobox value={f.driver_id} onChange={set("driver_id")} testId="assign-driver-select" placeholder="Seleziona driver" searchPlaceholder="Cerca driver..."
              options={drivers.filter((d) => d.active || d.id === item.driver_id).map((d) => ({ value: d.id, label: d.name }))} />
          </F>
          {(doubleCrew || item.co_driver_id) && <F label="Secondo autista (doppio equipaggio)" span>
            <PlateCombobox value={f.co_driver_id} onChange={set("co_driver_id")} testId="assign-co-driver-select" placeholder="Nessun secondo autista" searchPlaceholder="Cerca driver..."
              options={[{ value: NONE, label: "— Nessun secondo autista" }, ...drivers.filter((d) => (d.active || d.id === item.co_driver_id) && d.id !== f.driver_id).map((d) => ({ value: d.id, label: d.name }))]} />
          </F>}
          <F label="Furgone" span>
            <PlateCombobox value={f.vehicle_id} onChange={(v) => setF((p) => ({ ...p, vehicle_id: v, plate: v === NONE ? p.plate : "" }))} testId="assign-vehicle-select"
              options={[...(item.plate && !item.vehicle_id ? [{ value: NONE, label: `${item.plate} (non in flotta)` }] : []), ...vehicles.map((v) => ({ value: v.id, label: `${v.plate} · ${v.model || ""}` }))]} />
          </F>
          <F label="Telepass consegnato" span>
            <PlateCombobox value={f.telepass_id} onChange={set("telepass_id")} testId="assign-telepass-select"
              options={[{ value: NONE, label: "— Nessun Telepass" }, ...telepass.map((t) => ({ value: t.id, label: `${t.internal_number ? `N. int. ${t.internal_number} · ` : ""}${t.number}` }))]} />
            {f.telepass_id !== NONE && (() => { const t = telepass.find((x) => x.id === f.telepass_id); return t && <p className="text-xs text-slate-600" data-testid="assign-telepass-internal">Numero interno: <span className="font-mono-num font-semibold">{t.internal_number || "non impostato"}</span></p>; })()}
          </F>
          <F label="Gasolio (€)"><Input type="number" step="0.01" min="0" value={f.fuel_amount} onChange={set("fuel_amount")} data-testid="assign-fuel-input" /></F>
          <F label="Km totali"><p className="font-mono-num flex h-10 items-center text-lg font-semibold" data-testid="assign-km-total">{ks !== null && ke !== null && ke >= ks ? (ke - ks).toLocaleString("it-IT") : item.km_total ?? "—"}</p></F>
          <F label="Km partenza"><Input type="number" min="0" value={f.km_start} onChange={set("km_start")} data-testid="assign-km-start-input" /></F>
          <F label="Km arrivo"><Input type="number" min="0" value={f.km_end} onChange={set("km_end")} data-testid="assign-km-end-input" /></F>
          {hasCo && <>
            <F label="Cambio guida: km"><Input type="number" min="0" value={f.swap_km} onChange={set("swap_km")} placeholder="Facoltativo" data-testid="assign-swap-km-input" /></F>
            <F label="Cambio guida: ora"><Input type="time" value={f.swap_time} onChange={set("swap_time")} data-testid="assign-swap-time-input" /></F>
            {swapBad && <p className="text-xs text-rose-600 sm:col-span-2">Inserisci l'ora e km compresi tra partenza e arrivo.</p>}
          </>}
          <F label="Note" span><Input value={f.notes} onChange={set("notes")} data-testid="assign-notes-input" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy || !f.driver_id || !f.vehicle_id || (ks === null) !== (ke === null) || swapBad} data-testid="assign-save-button">{busy ? "Salvataggio..." : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
